import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiInfo, FiSun, FiSmartphone, FiMap, FiMessageCircle, FiCreditCard, FiNavigation } = FiIcons;

const tipDatabase = {
  PH: [
    {
      id: 1,
      title: "Commuter Etiquette",
      desc: "Always keep loose coins ready for Jeepney or Tricycle fares. Use 'Para po!' when you've reached your stop.",
      icon: FiMap,
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      id: 2,
      title: "Weather Ready",
      desc: "Localized thunderstorms are common in the afternoon. Always carry a compact umbrella, even if it's sunny.",
      icon: FiSun,
      color: "text-amber-600",
      bg: "bg-amber-50"
    },
    {
      id: 3,
      title: "Stay Connected",
      desc: "For the best experience in remote areas, ensure you have a local SIM from major telcos for stable GPS data.",
      icon: FiSmartphone,
      color: "text-indigo-600",
      bg: "bg-indigo-50"
    },
    {
      id: 4,
      title: "Local Courtesy",
      desc: "A simple 'Po' or 'Opo' when asking for directions shows respect and often gets you more helpful responses.",
      icon: FiMessageCircle,
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    }
  ],
  US: [
    {
      id: 1,
      title: "Tipping Culture",
      desc: "Standard tipping in restaurants is 15-20%. It is common to tip for most service-based interactions.",
      icon: FiCreditCard,
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    },
    {
      id: 2,
      title: "Emergency Number",
      desc: "Dial 911 immediately for police, fire, or medical emergencies. Most services are fast and reliable.",
      icon: FiInfo,
      color: "text-rose-600",
      bg: "bg-rose-50"
    },
    {
      id: 3,
      title: "Driving Rules",
      desc: "Remember to drive on the right side of the road. Pedestrians usually have the right of way at crosswalks.",
      icon: FiNavigation,
      color: "text-blue-600",
      bg: "bg-blue-50"
    }
  ],
  GB: [
    {
      id: 1,
      title: "Public Transport",
      desc: "Use contactless payment or an Oyster card for London transport. Queuing is taken very seriously here.",
      icon: FiMap,
      color: "text-red-600",
      bg: "bg-red-50"
    },
    {
      id: 2,
      title: "Weather Check",
      desc: "The weather changes rapidly. Layers are your best friend when exploring British cities.",
      icon: FiSun,
      color: "text-slate-600",
      bg: "bg-slate-50"
    }
  ],
  DEFAULT: [
    {
      id: 1,
      title: "Safe Exploration",
      desc: "Always keep your offline maps updated and share your live location with a trusted contact when exploring.",
      icon: FiMap,
      color: "text-indigo-600",
      bg: "bg-indigo-50"
    },
    {
      id: 2,
      title: "Digital Connectivity",
      desc: "Download local maps and translate packages before heading to areas with limited signal.",
      icon: FiSmartphone,
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      id: 3,
      title: "Local Essentials",
      desc: "Locate the nearest pharmacy and convenience store upon arrival in a new neighborhood.",
      icon: FiInfo,
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    }
  ]
};

const TravelTips = ({ countryCode, city }) => {
  const tips = tipDatabase[countryCode] || tipDatabase.DEFAULT;
  const displayLocation = city || countryCode || "Global";

  return (
    <div className="mb-12 px-2">
      <div className="flex items-center justify-between mb-4 px-1">
        <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-[0.2em]">
          Explorer's Guide <span className="text-indigo-600">• {displayLocation}</span>
        </h4>
        <div className="flex items-center gap-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">
          <SafeIcon icon={FiInfo} className="text-indigo-500" />
          Localized Insight
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {tips.map((tip, idx) => (
          <motion.div
            key={`${countryCode}-${tip.id}`}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            className="flex items-start gap-4 p-5 bg-white border border-slate-100 rounded-[2rem] high-contrast-shadow group"
          >
            <div className={`w-12 h-12 ${tip.bg} ${tip.color} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm transition-transform group-hover:scale-110`}>
              <SafeIcon icon={tip.icon} className="text-xl" />
            </div>
            <div className="space-y-1">
              <h5 className="font-black text-slate-950 text-sm tracking-tight">{tip.title}</h5>
              <p className="text-[11px] font-medium text-slate-500 leading-relaxed">
                {tip.desc}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-slate-950 rounded-[1.5rem] flex items-center justify-between">
        <p className="text-[9px] font-black text-white uppercase tracking-[0.15em] ml-2">
          Tailored for {displayLocation}
        </p>
        <div className="flex -space-x-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-6 h-6 rounded-full border-2 border-slate-950 bg-indigo-500 flex items-center justify-center text-[8px] font-bold text-white">
              {i}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TravelTips;